import { CheckCircleIcon, ArrowRightIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import type { AiExtractResponse } from '@shared/types';

interface ExtractedDataPreviewProps {
  data: AiExtractResponse;
  onCreateContract: () => void;
  onReset: () => void;
}

export function ExtractedDataPreview({
  data,
  onCreateContract,
  onReset,
}: ExtractedDataPreviewProps) {
  const confidenceColor =
    data.confidence >= 0.8
      ? 'text-green-700 bg-green-100'
      : data.confidence >= 0.6
      ? 'text-yellow-700 bg-yellow-100'
      : 'text-red-700 bg-red-100';

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-gray-900">Extracted Data</h2>
        <div className={`px-3 py-1 rounded-full text-sm font-medium ${confidenceColor}`}>
          {Math.round(data.confidence * 100)}% confidence
        </div>
      </div>

      <div className="space-y-6 max-h-[500px] overflow-y-auto">
        <section>
          <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <CheckCircleIcon className="w-5 h-5 text-green-600" />
            Executive Summary
          </h3>
          <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-md">
            {data.executiveSummary}
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <CheckCircleIcon className="w-5 h-5 text-green-600" />
            Deliverables ({data.deliverables.length})
          </h3>
          <div className="space-y-2">
            {data.deliverables.map((deliverable) => (
              <div key={deliverable.id} className="bg-gray-50 p-3 rounded-md text-sm">
                <p className="font-medium text-gray-900">{deliverable.title}</p>
                <p className="text-gray-600 mt-1">{deliverable.description}</p>
                {deliverable.dueDate && (
                  <p className="text-gray-500 text-xs mt-1">
                    Due: {new Date(deliverable.dueDate).toLocaleDateString()}
                  </p>
                )}
              </div>
            ))}
          </div>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <CheckCircleIcon className="w-5 h-5 text-green-600" />
            Milestones ({data.milestones.length})
          </h3>
          <div className="space-y-2">
            {data.milestones.map((milestone) => (
              <div key={milestone.id} className="bg-gray-50 p-3 rounded-md text-sm">
                <p className="font-medium text-gray-900">{milestone.title}</p>
                <p className="text-gray-600 mt-1">{milestone.description}</p>
                {milestone.dueDate && (
                  <p className="text-gray-500 text-xs mt-1">
                    Due: {new Date(milestone.dueDate).toLocaleDateString()}
                  </p>
                )}
              </div>
            ))}
          </div>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <CheckCircleIcon className="w-5 h-5 text-green-600" />
            Payments ({data.payments.length})
          </h3>
          <div className="space-y-2">
            {data.payments.map((payment) => (
              <div key={payment.id} className="bg-gray-50 p-3 rounded-md text-sm">
                <p className="font-medium text-gray-900">
                  {payment.amount} {payment.currency}
                </p>
                <p className="text-gray-600 mt-1">{payment.description}</p>
                {payment.dueDate && (
                  <p className="text-gray-500 text-xs mt-1">
                    Due: {new Date(payment.dueDate).toLocaleDateString()}
                  </p>
                )}
              </div>
            ))}
          </div>
        </section>
      </div>

      <div className="mt-6 flex gap-3">
        <button
          onClick={onCreateContract}
          className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-md hover:bg-primary-700 font-medium"
        >
          Create Contract
          <ArrowRightIcon className="w-5 h-5" />
        </button>
        <button
          onClick={onReset}
          className="px-6 py-3 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
        >
          <ArrowPathIcon className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

